import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify'
import {
  fetchTechnicalFormSubmissions,
  fetchTechnicalFormTemplates,
  TechnicalFormSubmission,
  TechnicalFormTemplate,
  deleteTechnicalFormSubmission,
  fetchFormCompanyLogo,
  FormCompanyLogo as CompanyLogo,
} from '../../core/forms_requests'
import {
  fetchQhseFormSubmissions,
  fetchQhseFormSubmissionById,
  deleteQhseFormSubmission,
  QHSEFormSubmission,
  QHSEFormFieldValuePayload,
  QHSEFormTemplate,
} from '../../../QHSE/core/forms_requests'
import { getCrewList } from '../../core/_requests'
import { Crew, Vessel } from '../../core/_models'
import { getManagementUsers } from '../../../Management/core/_requests'
import { ManagementUser } from '../../../Management/core/_models'
import { useAuth } from '../../../auth/core/Auth'
import {
  fetchAccessibleVesselsForCurrentUser,
  normalizeAccessibleVessels,
} from '../../../../shared/access/accessibleVessels'
import ConfirmDialog from '../../../../components/ConfirmDialog'
import { STATIC_FORMS } from './staticFormsConfig'
import { librarySubmissionIdentity, technicalSubmissionIdentity, staticSubmissionResponses } from './submissionPresentation'
import { librarySubmissionResponses } from './librarySubmissionResponses'
import { createSubmissionPdf, submissionPdfFileName } from './submissionPdf'
import { showErrorToast } from '../../../../shared/utils/apiErrorMessage'
import { RemarksCell, StructuredRemarksView } from '../../../QHSE/components/StructuredRemarksView'
import StaticFormRenderer from './StaticFormRenderer'
import FormCompanyLogo, { FormCompanyLogoDisplay } from './FormCompanyLogo'
import { applyPerPageWatermark } from '../../../Procurement/core/exportUtils'
import LibraryFormSubmissionModal from './LibraryFormSubmissionModal'
import { qhseLibraryApi } from '../../../QHSE/core/libraryApi'
import type { Fields, Reports } from '../../../QHSE/core/libraryModels'
import { TECH_PERM } from '../../core/technicalPermissions'
import { TechnicalAccessDenied } from '../TechnicalAccessDenied'
import { getAccessibleVessels, isVesselAssignedUserOnly } from '../../core/technicalScopeUtils'
import SearchableSelect from '../../../../shared/components/SearchableSelect'
import { useGlobalFilter } from '../../../../shared/context/GlobalFilterContext'
import { TableSkeleton } from '../../../../shared/components/LoadingSkeletons'

type SubmissionWithLookups = TechnicalFormSubmission & {
  vesselName?: string
  crewName?: string
  submittedByName?: string
  vessel?: { id?: number; fleet_name?: string }
  crew?: { id?: number; name?: string }
  submittedBy?: { id?: number; name?: string; username?: string }
}

type AnySubmission = TechnicalFormSubmission | QHSEFormSubmission

type SubmissionRow =
  | { source: 'technical'; submission: TechnicalFormSubmission }
  | { source: 'library'; submission: QHSEFormSubmission }

type LibraryFieldMeta = {
  menuName: string
  submenuName: string
  field: Fields
}

const HIDDEN_FORMATS = new Set(['xlsx', 'xls', 'json'])

const TechnicalFormsPage: React.FC = () => {
  const { currentUser, isSuperAdmin, hasPermission } = useAuth()

  const [templates, setTemplates] = useState<TechnicalFormTemplate[]>([])
  const [technicalSubmissions, setTechnicalSubmissions] = useState<TechnicalFormSubmission[]>([])
  const [librarySubmissions, setLibrarySubmissions] = useState<QHSEFormSubmission[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [crewList, setCrewList] = useState<Crew[]>([])
  const [users, setUsers] = useState<ManagementUser[]>([])

  const [loadingTemplates, setLoadingTemplates] = useState(true)
  const [loadingSubmissions, setLoadingSubmissions] = useState(true)
  const [deletingKey, setDeletingKey] = useState<string | null>(null)
  const [libraryTemplates, setLibraryTemplates] = useState<QHSEFormTemplate[]>([])

  const [vesselFilter, setVesselFilter] = useState('all')
  const [submittedByFilter, setSubmittedByFilter] = useState('all')
  const [submissionTemplateId, setSubmissionTemplateId] = useState<number | null>(null)

  const { state: globalState } = useGlobalFilter()

  useEffect(() => {
    if (globalState.vessel?.id) {
      setVesselFilter(String(globalState.vessel.id))
    } else {
      setVesselFilter('all')
    }
  }, [globalState.vessel])

  const [showFormModal, setShowFormModal] = useState(false)
  const [showLibraryFormModal, setShowLibraryFormModal] = useState(false)

  const [viewSubmission, setViewSubmission] = useState<TechnicalFormSubmission | null>(null)
  const [viewPayload, setViewPayload] = useState<any | null>(null)
  const [viewError, setViewError] = useState<string | null>(null)
  const [viewLibrarySubmission, setViewLibrarySubmission] = useState<QHSEFormSubmission | null>(
    null
  )
  const [viewLibraryFields, setViewLibraryFields] = useState<LibraryFieldMeta[]>([])
  const [viewLibraryValues, setViewLibraryValues] = useState<Record<number, string>>({})
  const [viewLibraryLoading, setViewLibraryLoading] = useState(false)
  const [viewLibraryError, setViewLibraryError] = useState<string | null>(null)

  const [pdfSubmission, setPdfSubmission] = useState<TechnicalFormSubmission | null>(null)
  const [pdfPayload, setPdfPayload] = useState<any | null>(null)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const [pdfLibrarySubmission, setPdfLibrarySubmission] = useState<QHSEFormSubmission | null>(null)
  const [pdfLibraryFields, setPdfLibraryFields] = useState<LibraryFieldMeta[]>([])
  const [pdfLibraryValues, setPdfLibraryValues] = useState<Record<number, string>>({})
  const [pdfActiveKey, setPdfActiveKey] = useState<string | null>(null)
  const pdfLock = useRef<string | null>(null)
  const pdfBusy = pdfActiveKey !== null
  const beginPdf = (source: SubmissionRow['source'], id: number) => {
    if (pdfLock.current !== null) return false
    const key = `${source}:${id}`
    pdfLock.current = key
    setPdfActiveKey(key)
    return true
  }
  const finishPdf = () => {
    pdfLock.current = null
    setPdfActiveKey(null)
  }
  const isPreparingPdf = (source: SubmissionRow['source'], id: number) =>
    pdfActiveKey === `${source}:${id}`
  const [pdfCompanyLogo, setPdfCompanyLogo] = useState<CompanyLogo | null>(null)
  const [pdfMode, setPdfMode] = useState(false)
  const pdfRef = useRef<HTMLDivElement | null>(null)
  const pdfLibraryRef = useRef<HTMLDivElement | null>(null)
  const libraryMenusCache = useRef<Map<number, Reports[]>>(new Map())
  const [confirmState, setConfirmState] = useState<{
    open: boolean
    title: string
    message: string
    tone?: 'danger' | 'primary'
    onConfirm: () => Promise<void> | void
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => undefined,
  })
  const [confirmBusy, setConfirmBusy] = useState(false)

  const effectiveCompanyGroupAdminId = useMemo(() => {
    if (currentUser?.companyGroupAdminId) return Number(currentUser.companyGroupAdminId)
    if (currentUser?.vessel?.companyGroupAdminId) return Number(currentUser.vessel.companyGroupAdminId)
    if (globalState.company?.type === 'company_group') return Number(globalState.company.id)
    if ((globalState.vessel as any)?.companyGroupAdminId) return Number((globalState.vessel as any).companyGroupAdminId)
    if ((globalState.vessel as any)?.companyGroupId) return Number((globalState.vessel as any).companyGroupId)
    if ((globalState.vessel as any)?.companyGroupAdmin?.id) return Number((globalState.vessel as any).companyGroupAdmin.id)
    if (globalState.company?.id) return Number(globalState.company.id)
    return undefined
  }, [currentUser, globalState.company, globalState.vessel])

  const effectiveCompanyAdminId = useMemo(() => {
    if (currentUser?.companyAdminId) return Number(currentUser.companyAdminId)
    if (currentUser?.vessel?.companyAdminId) return Number(currentUser.vessel.companyAdminId)
    if (globalState.company?.type === 'company_admin') return Number(globalState.company.id)
    if ((globalState.vessel as any)?.companyAdminId) return Number((globalState.vessel as any).companyAdminId)
    if ((globalState.vessel as any)?.companyAdmin?.id) return Number((globalState.vessel as any).companyAdmin.id)
    return undefined
  }, [currentUser, globalState.company, globalState.vessel])

  const isVesselOnlyUser = isVesselAssignedUserOnly(currentUser, isSuperAdmin)
  const companyAdminId = effectiveCompanyAdminId
  const companyGroupAdminId = effectiveCompanyGroupAdminId
  const canFillStaticForm = isVesselOnlyUser || hasPermission(TECH_PERM.CREATE_TECHNICAL_FORM)
  const canSubmitLibraryForms = canFillStaticForm
  const canDeleteSubmission = hasPermission(TECH_PERM.DELETE_TECHNICAL_FORM)
  const canDownloadFormPdf = hasPermission(TECH_PERM.DOWNLOAD_TECHNICAL_FORM)

  const normalizeCode = useMemo(
    () => (code?: string) => code ? code.toLowerCase().match(/[a-z0-9]+/g)?.[0] || '' : '',
    []
  )

  const sortByOrder = useCallback(<T,>(items: T[]) => {
    return [...items].sort((a, b) => {
      const aOrder =
        typeof (a as { order?: number }).order === 'number' ? (a as { order?: number }).order! : 0
      const bOrder =
        typeof (b as { order?: number }).order === 'number' ? (b as { order?: number }).order! : 0
      return aOrder - bOrder
    })
  }, [])

  const buildLibraryFieldMeta = useCallback(
    (menus: Reports[]) => {
      const meta: LibraryFieldMeta[] = []
      sortByOrder(menus.filter((menu) => menu.isActive)).forEach((menu) => {
        sortByOrder(menu.submenus || [])
          .filter((submenu) => submenu.isActive)
          .forEach((submenu) => {
            sortByOrder(submenu.fields || [])
              .filter((field) => field.isActive)
              .forEach((field) => {
                meta.push({
                  menuName: menu.name,
                  submenuName: submenu.name,
                  field,
                })
              })
          })
      })
      return meta
    },
    [sortByOrder]
  )

  const buildLibraryValueMap = useCallback((values?: QHSEFormFieldValuePayload[] | null) => {
    const map: Record<number, string> = {}
    values?.forEach((value) => {
      if (!value.fieldId) return
      map[value.fieldId] = value.valueText ?? ''
    })
    return map
  }, [])

  const formatLibraryValue = useCallback((raw?: string) => {
    if (!raw) return '-'
    const trimmed = raw.trim()
    if (!trimmed) return '-'
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) return parsed.join(', ')
      if (parsed && typeof parsed === 'object') return JSON.stringify(parsed)
      return String(parsed)
    } catch {
      return trimmed
    }
  }, [])

  const getLibraryTemplateMenus = useCallback(async (templateId: number) => {
    const cached = libraryMenusCache.current.get(templateId)
    if (cached) return cached
    const menus = await qhseLibraryApi.getReportsForTemplate(templateId)
    libraryMenusCache.current.set(templateId, menus)
    return menus
  }, [])

  const submissionContext = useMemo(() => {
    const resolvedCompanyAdminId = effectiveCompanyAdminId
    const resolvedCompanyGroupAdminId = effectiveCompanyGroupAdminId
    const resolvedVesselId =
      currentUser?.vessel?.id ?? (globalState.vessel?.id ? Number(globalState.vessel.id) : undefined)

    return {
      crewId: isVesselOnlyUser ? currentUser?.roleEntityId ?? undefined : undefined,
      vesselId: resolvedVesselId,
      companyId: resolvedCompanyAdminId ?? resolvedCompanyGroupAdminId,
      companyAdminId: resolvedCompanyAdminId,
      companyGroupAdminId: resolvedCompanyGroupAdminId,
    }
  }, [currentUser, isVesselOnlyUser, effectiveCompanyAdminId, effectiveCompanyGroupAdminId, globalState.vessel])

  const submissionFilterParams = useMemo(() => {
    const params: {
      companyId?: number
      companyAdminId?: number
      companyGroupAdminId?: number
      crewId?: number
      vesselId?: number
    } = {}
    if (submissionContext.companyId) params.companyId = submissionContext.companyId
    if (submissionContext.companyAdminId) params.companyAdminId = submissionContext.companyAdminId
    if (submissionContext.companyGroupAdminId)
      params.companyGroupAdminId = submissionContext.companyGroupAdminId
    if (submissionContext.vesselId) params.vesselId = submissionContext.vesselId
    if (isVesselOnlyUser && submissionContext.crewId) params.crewId = submissionContext.crewId
    return params
  }, [
    submissionContext.companyAdminId,
    submissionContext.companyGroupAdminId,
    submissionContext.companyId,
    submissionContext.crewId,
    submissionContext.vesselId,
    isVesselOnlyUser,
  ])

  const scopeSubmissions = useCallback(
    <T extends AnySubmission>(list: T[]) => {
      return list.filter((submission) => {
        const technicalCompanyId = (submission as TechnicalFormSubmission).companyId
        const libraryCompanyAdminId = (submission as QHSEFormSubmission).companyAdminId
        const libraryCompanyGroupAdminId = (submission as QHSEFormSubmission).companyGroupAdminId

        if (
          submissionFilterParams.companyId &&
          technicalCompanyId != null &&
          technicalCompanyId !== submissionFilterParams.companyId
        ) {
          return false
        }

        if (submissionFilterParams.companyAdminId || submissionFilterParams.companyGroupAdminId) {
          const companyAdminMatch = submissionFilterParams.companyAdminId
            ? libraryCompanyAdminId === submissionFilterParams.companyAdminId
            : false
          const companyGroupMatch = submissionFilterParams.companyGroupAdminId
            ? libraryCompanyGroupAdminId === submissionFilterParams.companyGroupAdminId
            : false

          const hasLibraryCompanyValues =
            libraryCompanyAdminId != null || libraryCompanyGroupAdminId != null
          if (hasLibraryCompanyValues && !companyAdminMatch && !companyGroupMatch) {
            return false
          }
        }

        if (
          submissionFilterParams.vesselId &&
          submission.vesselId !== submissionFilterParams.vesselId
        ) {
          return false
        }
        if (submissionFilterParams.crewId && submission.crewId !== submissionFilterParams.crewId) {
          return false
        }
        return true
      }) as T[]
    },
    [
      submissionFilterParams.companyAdminId,
      submissionFilterParams.companyGroupAdminId,
      submissionFilterParams.companyId,
      submissionFilterParams.crewId,
      submissionFilterParams.vesselId,
    ]
  )

  const staticFormMap = useMemo(() => {
    const map = new Map<string, typeof STATIC_FORMS[number]>()
    STATIC_FORMS.forEach((form) => map.set(normalizeCode(form.code), form))
    return map
  }, [normalizeCode])

  const templateById = useMemo(() => {
    const map = new Map<number, TechnicalFormTemplate>()
    templates.forEach((template) => map.set(template.id, template))
    return map
  }, [templates])

  const libraryTemplateById = useMemo(() => {
    const map = new Map<number, QHSEFormTemplate>()
    libraryTemplates.forEach((template) => map.set(template.id, template))
    return map
  }, [libraryTemplates])

  const loadTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true)
      const data = await fetchTechnicalFormTemplates(
        submissionContext.companyId ? { companyId: submissionContext.companyId } : undefined
      )
      setTemplates(data)
      return data
    } catch (err: any) {
      toast.error('Unable to load forms')
      return null
    } finally {
      setLoadingTemplates(false)
    }
  }, [submissionContext.companyId])

  const loadLibraryTemplates = useCallback(async () => {
    try {
      const data = await qhseLibraryApi.getTemplateList(
        submissionContext.companyGroupAdminId || submissionContext.companyAdminId
          ? {
            companyGroupAdminId: submissionContext.companyGroupAdminId ?? undefined,
            companyAdminId: submissionContext.companyAdminId ?? undefined,
          }
          : undefined
      )
      setLibraryTemplates(data as QHSEFormTemplate[])
      return data
    } catch (err: any) {
      toast.error('Unable to load library templates')
      return []
    }
  }, [submissionContext.companyAdminId, submissionContext.companyGroupAdminId])

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  useEffect(() => {
    loadLibraryTemplates()
  }, [loadLibraryTemplates])

  const loadAllSubmissions = useCallback(async () => {
    try {
      setLoadingSubmissions(true)
      const technicalPromise = templates.length
        ? Promise.all(
          templates.map((template) =>
            fetchTechnicalFormSubmissions(template.id, submissionFilterParams).catch(() => [])
          )
        ).then((lists) => lists.flat())
        : Promise.resolve([])

      const scopedCompanyGroupId = submissionContext.companyGroupAdminId ?? companyGroupAdminId
      const libraryPromise = scopedCompanyGroupId
        ? fetchQhseFormSubmissions({
          companyGroupId: scopedCompanyGroupId,
        }).catch(() => [])
        : hasPermission(TECH_PERM.READ_TECHNICAL_FORM)
          ? fetchQhseFormSubmissions().catch(() => [])
          : Promise.resolve([])

      const [technicalMerged, libraryMerged] = await Promise.all([technicalPromise, libraryPromise])

      setTechnicalSubmissions(scopeSubmissions(technicalMerged))
      setLibrarySubmissions(scopeSubmissions(libraryMerged))
    } catch (err: any) {
      toast.error('Unable to load submissions')
    } finally {
      setLoadingSubmissions(false)
    }
  }, [
    companyGroupAdminId,
    hasPermission,
    scopeSubmissions,
    submissionContext.companyGroupAdminId,
    submissionFilterParams,
    templates,
  ])

  useEffect(() => {
    if (loadingTemplates) return
    loadAllSubmissions()
  }, [loadAllSubmissions, loadingTemplates])

  const combinedSubmissions = useMemo<SubmissionRow[]>(() => {
    const rows: SubmissionRow[] = [
      ...technicalSubmissions.map(
        (submission): SubmissionRow => ({ source: 'technical', submission })
      ),
      ...librarySubmissions.map((submission): SubmissionRow => ({ source: 'library', submission })),
    ]
    return rows.sort((a, b) => {
      const aTime = a.submission.createdAt ? new Date(a.submission.createdAt).getTime() : 0
      const bTime = b.submission.createdAt ? new Date(b.submission.createdAt).getTime() : 0
      return bTime - aTime
    })
  }, [librarySubmissions, technicalSubmissions])

  const submissions = useMemo<AnySubmission[]>(
    () => combinedSubmissions.map((row) => row.submission),
    [combinedSubmissions]
  )

  useEffect(() => {
    if (isVesselOnlyUser) return
    let active = true
    const tasks: Promise<void>[] = []

    if (!vessels.length) {
      tasks.push(
        fetchAccessibleVesselsForCurrentUser()
          .then((list) => {
            if (!active) return
            setVessels(normalizeAccessibleVessels(list))
          })
          .catch((err: any) => {
            if (!active) return
            toast.error('Unable to load vessels')
          })
      )
    }

    if (!crewList.length) {
      tasks.push(
        getCrewList()
          .then((list) => {
            if (!active) return
            setCrewList(Array.isArray(list) ? list : [])
          })
          .catch(() => undefined)
      )
    }

    if (!users.length) {
      tasks.push(
        getManagementUsers()
          .then((list) => {
            if (!active) return
            setUsers(Array.isArray(list) ? list : [])
          })
          .catch(() => undefined)
      )
    }

    if (!tasks.length) return
    Promise.all(tasks)
      .catch(() => undefined)
      .finally(() => {
        if (!active) return
      })

    return () => {
      active = false
    }
  }, [crewList.length, isVesselOnlyUser, users.length, vessels.length])

  const resolveVisibleFormat = useCallback((format?: string | null) => {
    if (!format) return '-'
    return HIDDEN_FORMATS.has(format.toLowerCase()) ? '-' : format
  }, [])

  const scopedVessels = useMemo(() => {
    if (isVesselOnlyUser) {
      const crewVessel = currentUser?.vessel as Vessel | null | undefined
      return crewVessel ? [crewVessel] : []
    }
    return getAccessibleVessels(vessels, currentUser, isSuperAdmin)
  }, [currentUser, isSuperAdmin, isVesselOnlyUser, vessels])

  const userNameById = useMemo(() => {
    const map = new Map<number, string>()
    users.forEach((u) => {
      if (!u?.userId) return
      map.set(u.userId, u.fullName || u.username)
    })
    if (currentUser?.id && (currentUser?.username || currentUser?.roleEntityName)) {
      map.set(currentUser.id, currentUser.roleEntityName || currentUser.username)
    }
    return map
  }, [currentUser?.id, currentUser?.roleEntityName, currentUser?.username, users])

  const crewNameById = useMemo(() => {
    const map = new Map<number, string>()
    crewList.forEach((crew) => {
      if (!crew?.id) return
      map.set(crew.id, crew.name)
    })
    const currentCrewId = currentUser?.roleEntityId
    const currentCrewName = currentUser?.roleEntityName || currentUser?.username
    if (isVesselOnlyUser && currentCrewId && currentCrewName && !map.has(currentCrewId)) {
      map.set(currentCrewId, currentCrewName)
    }
    return map
  }, [
    crewList,
    currentUser?.roleEntityId,
    currentUser?.roleEntityName,
    currentUser?.username,
    isVesselOnlyUser,
  ])

  const vesselNameById = useMemo(() => {
    const map = new Map<number, string>()
    vessels.forEach((vessel) => {
      if (!vessel?.id) return
      const name =
        (vessel as any).fleet_name ||
        (vessel as any).fleetName ||
        (vessel as any).name ||
        (vessel as any).vesselName
      if (name) map.set(vessel.id, name)
    })
    scopedVessels.forEach((vessel) => {
      if (!vessel?.id) return
      const name =
        (vessel as any).fleet_name ||
        (vessel as any).fleetName ||
        (vessel as any).name ||
        (vessel as any).vesselName
      if (name) map.set(vessel.id, name)
    })
    const currentVesselId = currentUser?.vessel?.id
    const currentVesselName =
      currentUser?.vessel?.fleet_name || (currentUser?.vessel as any)?.name
    if (currentVesselId && currentVesselName && !map.has(currentVesselId)) {
      map.set(currentVesselId, currentVesselName)
    }
    return map
  }, [currentUser?.vessel, scopedVessels, vessels])

  const getVesselId = (submission: AnySubmission) => {
    const withLookups = submission as SubmissionWithLookups
    return submission.vesselId ?? withLookups.vessel?.id
  }

  const getSubmitterKey = (submission: AnySubmission) => {
    const withLookups = submission as SubmissionWithLookups
    const crewId = submission.crewId ?? withLookups.crew?.id
    if (crewId) return `crew:${crewId}`
    const userId =
      (submission as TechnicalFormSubmission).submittedByUserId ??
      (submission as QHSEFormSubmission).createdByUserId ??
      withLookups.submittedBy?.id
    if (userId) return `user:${userId}`
    return ''
  }

  const resolveVesselLabel = (submission: AnySubmission) => {
    const withLookups = submission as SubmissionWithLookups
    const directName =
      (submission as any).vesselName ||
      withLookups.vesselName ||
      withLookups.vessel?.fleet_name ||
      (withLookups.vessel as any)?.name
    if (directName) return directName

    // Fallback: extract vessel name from submissionData (for previous data)
    const rawData = (submission as TechnicalFormSubmission).submissionData
    if (rawData) {
      try {
        const parsed = JSON.parse(rawData)
        const d = parsed?.data && typeof parsed.data === 'object' ? parsed.data : parsed
        const vName =
          d?.vesselName ||
          d?.vessel ||
          d?.vessel_name ||
          d?.shipName ||
          d?.ship_name ||
          parsed?.vesselName ||
          parsed?.vessel
        if (vName && typeof vName === 'string' && vName.trim()) {
          return vName.trim()
        }
      } catch { }
    }

    const vesselId = getVesselId(submission)
    if (vesselId && vesselNameById.has(vesselId)) {
      return vesselNameById.get(vesselId)!
    }
    if (!vesselId) return '-'
    return `Vessel #${vesselId}`
  }

  const resolveSubmitterLabel = (submission: AnySubmission) => {
    const withLookups = submission as SubmissionWithLookups
    const crewId = submission.crewId ?? withLookups.crew?.id
    if (crewId) {
      const directCrewName =
        (submission as any).crewName ||
        withLookups.crewName ||
        withLookups.crew?.name
      if (directCrewName) return directCrewName
      if (crewNameById.has(crewId)) return crewNameById.get(crewId)!
      return `Crew #${crewId}`
    }
    const userId =
      (submission as TechnicalFormSubmission).submittedByUserId ??
      (submission as QHSEFormSubmission).createdByUserId ??
      withLookups.submittedBy?.id
    if (userId) {
      const directUserName =
        (submission as any).submittedByName ||
        withLookups.submittedByName ||
        withLookups.submittedBy?.username ||
        withLookups.submittedBy?.name
      if (directUserName) return directUserName
      if (userNameById.has(userId)) return userNameById.get(userId)!
      return `User #${userId}`
    }

    // Fallback: extract submitter from submissionData (for previous data)
    const rawData = (submission as TechnicalFormSubmission).submissionData
    if (rawData) {
      try {
        const parsed = JSON.parse(rawData)
        const d = parsed?.data && typeof parsed.data === 'object' ? parsed.data : parsed
        const sName =
          d?.submittedBy ||
          d?.crewName ||
          d?.crew ||
          d?.officerName ||
          d?.masterName ||
          d?.chiefEngineerName ||
          parsed?.submittedBy
        if (sName && typeof sName === 'string' && sName.trim()) {
          return sName.trim()
        }
      } catch { }
    }

    return '-'
  }

  const vesselOptions = useMemo(() => {
    const unique = new Map<string, string>()
    submissions.forEach((submission) => {
      if (submissionTemplateId) {
        const templateId = submission.templateId ?? null
        if (templateId !== submissionTemplateId) return
      }
      const vesselId = getVesselId(submission)
      if (!vesselId) return
      const label = resolveVesselLabel(submission)
      unique.set(String(vesselId), label)
    })
    return Array.from(unique.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [submissions, submissionTemplateId, vesselNameById])

  const submitterOptions = useMemo(() => {
    const unique = new Map<string, string>()
    submissions.forEach((submission) => {
      if (submissionTemplateId) {
        const templateId = submission.templateId ?? null
        if (templateId !== submissionTemplateId) return
      }
      if (vesselFilter !== 'all') {
        const vesselId = getVesselId(submission)
        if (!vesselId || String(vesselId) !== vesselFilter) return
      }
      const key = getSubmitterKey(submission)
      if (!key) return
      const label = resolveSubmitterLabel(submission)
      unique.set(key, label)
    })
    return Array.from(unique.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [crewNameById, submissions, submissionTemplateId, vesselFilter])

  const filteredSubmissions = useMemo(() => {
    return combinedSubmissions.filter((row) => {
      const submission = row.submission
      const templateOk =
        !submissionTemplateId ||
        (row.source === 'technical' && submission.templateId === submissionTemplateId)
      const vesselId = getVesselId(submission)
      const vesselOk =
        vesselFilter === 'all' || (vesselId ? String(vesselId) === vesselFilter : false)
      const submitterKey = getSubmitterKey(submission)
      const submitterOk =
        submittedByFilter === 'all' || (submitterKey ? submitterKey === submittedByFilter : false)
      return templateOk && vesselOk && submitterOk
    })
  }, [combinedSubmissions, submissionTemplateId, submittedByFilter, vesselFilter])

  const refreshSubmissions = async () => {
    await loadLibraryTemplates()
    await loadAllSubmissions()
  }

  const resolveFileUrl = (url?: string | null) => {
    if (!url) return ''
    if (/^https?:\/\//i.test(url)) return url
    const normalized = url.startsWith('/') ? url : `/${url}`
    return `${window.location.origin}${normalized}`
  }

  const handleTemplateCreated = (tpl: TechnicalFormTemplate) => {
    setTemplates((prev) => (prev.find((t) => t.id === tpl.id) ? prev : [...prev, tpl]))
    toast.success('Template created')
  }

  const handleDelete = async (submissionId: number) => {
    setConfirmState({
      open: true,
      title: 'Delete submission',
      message: 'Delete this submission? This cannot be undone.',
      tone: 'danger',
      onConfirm: async () => {
        setDeletingKey(`technical-${submissionId}`)
        await deleteTechnicalFormSubmission(submissionId, { userId: currentUser?.id })
        toast.success('Submission deleted')
        await refreshSubmissions()
      },
    })
  }

  const openView = (submission: TechnicalFormSubmission) => {
    setViewLibrarySubmission(null)
    setViewSubmission(submission)
    setViewError(null)

    if (!submission.submissionData) {
      setViewError('No inline data stored for this submission. Download the file to view it.')
      setViewPayload(null)
      return
    }

    try {
      setViewPayload(JSON.parse(submission.submissionData))
    } catch {
      setViewPayload(null)
      setViewError('Unable to read saved form data. Please download the file instead.')
    }
  }

  const handleDeleteLibrary = async (submissionId: number) => {
    setConfirmState({
      open: true,
      title: 'Delete submission',
      message: 'Delete this submission? This cannot be undone.',
      tone: 'danger',
      onConfirm: async () => {
        setDeletingKey(`library-${submissionId}`)
        await deleteQhseFormSubmission(submissionId, { userId: currentUser?.id })
        toast.success('Submission deleted')
        await refreshSubmissions()
      },
    })
  }

  const openLibraryView = async (submission: QHSEFormSubmission) => {
    setViewSubmission(null)
    setViewPayload(null)
    setViewError(null)
    setViewLibrarySubmission(submission)
    setViewLibraryError(null)
    setViewLibraryLoading(true)
    setViewLibraryFields([])
    setViewLibraryValues({})
    try {
      const detail = await fetchQhseFormSubmissionById(submission.id)
      const templateId = detail.templateId ?? submission.templateId
      if (!templateId) {
        throw new Error('Template not found for this submission')
      }
      const menus = await getLibraryTemplateMenus(templateId)
      setViewLibraryFields(buildLibraryFieldMeta(menus))
      setViewLibraryValues(buildLibraryValueMap(detail.values))
      setViewLibrarySubmission(detail)
    } catch (err: any) {
      setViewLibraryError('Unable to load submission details')
    } finally {
      setViewLibraryLoading(false)
    }
  }

  const closeView = () => {
    setViewSubmission(null)
    setViewPayload(null)
    setViewError(null)
  }

  const closeLibraryView = () => {
    setViewLibrarySubmission(null)
    setViewLibraryFields([])
    setViewLibraryValues({})
    setViewLibraryError(null)
  }

  const viewStaticForm = useMemo(() => {
    if (!viewPayload?.formCode) return null
    return staticFormMap.get(normalizeCode(viewPayload.formCode)) || null
  }, [normalizeCode, staticFormMap, viewPayload?.formCode])

  const viewTemplate = useMemo(() => {
    if (!viewSubmission) return null
    return templateById.get(viewSubmission.templateId) || null
  }, [templateById, viewSubmission])

  const pdfStaticForm = useMemo(() => {
    if (!pdfPayload?.formCode) return null
    return staticFormMap.get(normalizeCode(pdfPayload.formCode)) || null
  }, [normalizeCode, pdfPayload?.formCode, staticFormMap])

  const pdfTemplate = useMemo(() => {
    if (!pdfSubmission) return null
    return templateById.get(pdfSubmission.templateId) || null
  }, [pdfSubmission, templateById])

  const formatChecklistAnswer = (value: unknown) => {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      if (normalized === 'na' || normalized === 'n/a') return 'NA'
      if (normalized === 'yes' || normalized === 'y') return 'Yes'
      if (normalized === 'no' || normalized === 'n') return 'No'
    }
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (value == null) return 'No'
    return String(value)
  }

  const renderFieldValue = (
    field: typeof STATIC_FORMS[number]['fields'][number],
    payload?: any
  ) => {
    const source = payload || viewPayload
    if (!source?.data) return '-'
    const val = source.data[field.name]
    if (/remarks?/i.test(field.name + ' ' + field.label)) {
      return <StructuredRemarksView text={val == null ? '' : String(val)} emptyMessage='-' />
    }
    if (field.type === 'checklist' && field.items) {
      const checklist = (val as Record<string, unknown>) || {}
      return (
        <ul className='mb-0 ps-3'>
          {field.items.map((item) => (
            <li key={item}>
              {item}: <span className='fw-semibold'>{formatChecklistAnswer(checklist[item])}</span>
            </li>
          ))}
        </ul>
      )
    }
    return (val as string) || '-'
  }

  const renderPdfFieldValue = (
    field: typeof STATIC_FORMS[number]['fields'][number],
    payload: any
  ): React.ReactNode => {
    if (!payload?.data) return '-'
    const val = payload.data[field.name]

    if (field.type === 'checklist' && field.items) {
      const checklist = (val as Record<string, unknown>) || {}
      return (
        <div style={{ display: 'grid', gap: '6px' }}>
          {field.items.map((item) => (
            <div
              key={item}
              style={{
                fontSize: '11px',
                lineHeight: 1.4,
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                breakInside: 'avoid',
                pageBreakInside: 'avoid',
              }}
            >
              <span style={{ color: '#1e1e2d' }}>{item}: </span>
              <span style={{ fontWeight: 600 }}>{formatChecklistAnswer(checklist[item])}</span>
            </div>
          ))}
        </div>
      )
    }

    const text = (val as string) || '-'
    return (
      <div
        style={{
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        {text}
      </div>
    )
  }

  const buildPdfResponseRows = (fields: typeof STATIC_FORMS[number]['fields'], payload: any) => {
    const rows: React.ReactNode[] = []
    const buffer: typeof STATIC_FORMS[number]['fields'] = []
    const pushPair = (
      left?: typeof STATIC_FORMS[number]['fields'][number],
      right?: typeof STATIC_FORMS[number]['fields'][number]
    ) => {
      rows.push(
        <tr
          key={`${left?.name || 'empty'}-${right?.name || 'empty'}`}
          style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}
        >
          <td style={{ padding: '6px', verticalAlign: 'top', width: '50%' }}>
            {left ? (
              <div
                className='pdf-avoid'
                style={{
                  border: '1px solid #e4e6ef',
                  borderRadius: '6px',
                  padding: '10px',
                  breakInside: 'avoid',
                  pageBreakInside: 'avoid',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '12px' }}>
                  {left.label}
                </div>
                <div
                  style={{
                    color: '#7e8299',
                    fontSize: '11px',
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {renderPdfFieldValue(left, payload)}
                </div>
              </div>
            ) : null}
          </td>
          <td style={{ padding: '6px', verticalAlign: 'top', width: '50%' }}>
            {right ? (
              <div
                className='pdf-avoid'
                style={{
                  border: '1px solid #e4e6ef',
                  borderRadius: '6px',
                  padding: '10px',
                  breakInside: 'avoid',
                  pageBreakInside: 'avoid',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '12px' }}>
                  {right.label}
                </div>
                <div
                  style={{
                    color: '#7e8299',
                    fontSize: '11px',
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {renderPdfFieldValue(right, payload)}
                </div>
              </div>
            ) : null}
          </td>
        </tr>
      )
    }

    fields.forEach((field) => {
      if (field.type === 'checklist') {
        if (buffer.length) {
          pushPair(buffer[0], buffer[1])
          buffer.length = 0
        }
        rows.push(
          <tr key={field.name} style={{ breakInside: 'auto', pageBreakInside: 'auto' }}>
            <td colSpan={2} style={{ padding: '6px', verticalAlign: 'top' }}>
              <div
                style={{
                  border: '1px solid #e4e6ef',
                  borderRadius: '6px',
                  padding: '10px',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '6px', fontSize: '12px' }}>
                  {field.label}
                </div>
                <div style={{ color: '#7e8299', fontSize: '11px' }}>
                  {renderPdfFieldValue(field, payload)}
                </div>
              </div>
            </td>
          </tr>
        )
      } else {
        buffer.push(field)
        if (buffer.length === 2) {
          pushPair(buffer[0], buffer[1])
          buffer.length = 0
        }
      }
    })

    if (buffer.length) {
      pushPair(buffer[0], undefined)
    }

    return rows
  }

  const renderLibraryValueRows = useCallback(
    (items: LibraryFieldMeta[], valuesMap: Record<number, string>) => {
      const rows: React.ReactNode[] = []
      let lastMenu = ''
      let lastSubmenu = ''
      items.forEach((item) => {
        if (item.menuName !== lastMenu) {
          rows.push(
            <tr key={`menu-${item.menuName}`} className='table-light'>
              <td colSpan={2} style={{ fontWeight: 700 }}>
                {item.menuName}
              </td>
            </tr>
          )
          lastMenu = item.menuName
          lastSubmenu = ''
        }
        if (item.submenuName !== lastSubmenu) {
          rows.push(
            <tr key={`submenu-${item.menuName}-${item.submenuName}`} className='table-secondary'>
              <td colSpan={2} style={{ fontWeight: 600 }}>
                {item.submenuName}
              </td>
            </tr>
          )
          lastSubmenu = item.submenuName
        }

        rows.push(
          <tr key={`field-${item.field.id}`}>
            <td style={{ width: '45%' }}>{item.field.label}</td>
            <td style={{ width: '55%' }}>
              {/remarks?/i.test(item.field.label) ? (
                <RemarksCell text={formatLibraryValue(valuesMap[item.field.id])} title={item.field.label} />
              ) : formatLibraryValue(valuesMap[item.field.id])}
            </td>
          </tr>
        )
      })
      return rows
    },
    [formatLibraryValue]
  )

  const badgeForStatus = (st?: string) => {
    const v = (st || '').toLowerCase()
    if (!v) return <span className='badge badge-light'>-</span>
    if (v.includes('approved')) return <span className='badge badge-light-success'>{st}</span>
    if (v.includes('rejected')) return <span className='badge badge-light-danger'>{st}</span>
    if (v.includes('pending')) return <span className='badge badge-light-warning'>{st}</span>
    return <span className='badge badge-light-primary'>{st}</span>
  }

  const pdfMetadata = (submission: AnySubmission): [string, string][] => [
    ['Vessel', resolveVesselLabel(submission)],
    ['Submitted By', resolveSubmitterLabel(submission)],
    ['Status', submission.status || '-'],
    ['Submitted', submission.createdAt ? new Date(submission.createdAt).toLocaleString() : '-'],
    ['Remarks', submission.remarks || '-'],
  ]

  const parseSubmissionPayload = (submission: TechnicalFormSubmission) => {
    if (!submission.submissionData) {
      return { payload: null, error: 'No inline data stored for this submission.' }
    }
    try {
      return { payload: JSON.parse(submission.submissionData), error: null }
    } catch {
      return { payload: null, error: 'Unable to read saved form data.' }
    }
  }

  const buildPdfFileName = (submission: TechnicalFormSubmission, payload?: any) => {
    const template = templateById.get(submission.templateId)
    const raw = payload?.formCode || template?.code || `submission_${submission.id}`
    const safe = String(raw).replace(/[^a-z0-9_-]+/gi, '_')
    return `${safe}_${submission.id}.pdf`
  }

  const downloadSubmissionPdf = async (submission: TechnicalFormSubmission) => {
    if (pdfLock.current !== null) return
    const { payload, error } = parseSubmissionPayload(submission)
    if (!payload) {
      toast.error(error || 'Unable to generate PDF for this submission.')
      return
    }
    if (!beginPdf('technical', submission.id)) return
    setPdfCompanyLogo(null)
    const vesselId = getVesselId(submission)
    const company = vesselId ? await fetchFormCompanyLogo(vesselId).catch(() => null) : null
    setPdfCompanyLogo(company)
    setPdfLibrarySubmission(null)
    setPdfLibraryFields([])
    setPdfLibraryValues({})
    setPdfSubmission(submission)
    setPdfPayload(payload)
    setPdfError(null)

    setTimeout(async () => {
      try {
        const element = pdfRef.current
        if (!element) throw new Error('PDF content is unavailable')

        if (document?.fonts?.ready) await document.fonts.ready
        await new Promise<void>((resolve) =>
          requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
        )

        const html2pdf = (await import('html2pdf.js')).default

        await html2pdf()
          .from(element)
          .set({
            filename: buildPdfFileName(submission, payload),
            jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' },
            html2canvas: {
              scale: 2,
              useCORS: true,
              backgroundColor: '#ffffff',
              windowWidth: element.scrollWidth,
            },
            margin: [28, 28, 28, 28],
            pagebreak: { mode: ['css', 'legacy'], avoid: ['.pdf-avoid', '.pdf-card', 'tr'] },
          })
          .toPdf()
          .get('pdf')
          .then(async (pdf: any) => { await applyPerPageWatermark(pdf, element) })
          .save()
      } catch {
        toast.error('Unable to generate PDF for this submission.')
      } finally {
        finishPdf()
      }
    }, 250)
  }

  const buildLibraryPdfFileName = (submission: QHSEFormSubmission) => {
    const template = libraryTemplateById.get(submission.templateId ?? 0)
    const raw = template?.name || template?.formType || `library_submission_${submission.id}`
    const safe = String(raw).replace(/[^a-z0-9_-]+/gi, '_')
    return `${safe}_${submission.id}.pdf`
  }

  const downloadLibrarySubmissionPdf = async (submission: QHSEFormSubmission) => {
    if (!beginPdf('library', submission.id)) return
    setPdfSubmission(null)
    setPdfPayload(null)
    setPdfError(null)
    setPdfCompanyLogo(null)
    try {
      const detail = await fetchQhseFormSubmissionById(submission.id)
      const vesselId = getVesselId(detail)
      const company = vesselId ? await fetchFormCompanyLogo(vesselId).catch(() => null) : null
      setPdfCompanyLogo(company)
      const templateId = detail.templateId ?? submission.templateId
      if (!templateId) throw new Error('Template not found for this submission')
      const menus = await getLibraryTemplateMenus(templateId)
      setPdfLibrarySubmission(detail)
      setPdfLibraryFields(buildLibraryFieldMeta(menus))
      setPdfLibraryValues(buildLibraryValueMap(detail.values))

      setTimeout(async () => {
        try {
          const element = pdfLibraryRef.current
          if (!element) throw new Error('PDF content is unavailable')

          if (document?.fonts?.ready) await document.fonts.ready
          await new Promise<void>((resolve) =>
            requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
          )

          const html2pdf = (await import('html2pdf.js')).default

          await html2pdf()
            .from(element)
            .set({
              filename: buildLibraryPdfFileName(detail),
              jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' },
              html2canvas: {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                windowWidth: element.scrollWidth,
              },
              margin: [28, 28, 28, 28],
              pagebreak: { mode: ['css', 'legacy'], avoid: ['.pdf-avoid', '.pdf-card', 'tr'] },
            })
            .toPdf()
            .get('pdf')
            .then(async (pdf: any) => { await applyPerPageWatermark(pdf, element) })
            .save()
        } catch {
          toast.error('Unable to generate PDF for this submission.')
        } finally {
          finishPdf()
        }
      }, 250)
    } catch (err: any) {
      toast.error('Unable to generate PDF for this submission.')
      finishPdf()
    }
  }

  if (!hasPermission(TECH_PERM.READ_TECHNICAL_FORM)) {
    return (
      <TechnicalAccessDenied
        title='Forms & Checklists'
        message='You do not have permission to view technical forms.'
      />
    )
  }

  return (
    <>
      <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
        <div className='d-flex flex-column flex-column-fluid'>
          <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
            <div className='card mb-5'>
              <div className='card-header border-0 pt-6 d-flex justify-content-between align-items-center bg-white'>
                <div>
                  <h3 className='card-label text-dark fw-bold'>Forms &amp; Checklists</h3>
                  <p className='text-muted mb-2'>
                    Manage company forms, fill online, and review submissions.
                  </p>
                </div>
                <div className='card-toolbar d-flex gap-2'>
                  {canFillStaticForm ? (
                    <button
                      type='button'
                      className='btn btn_primary'
                      onClick={() => setShowFormModal(true)}
                    >
                      Form Fillup
                    </button>
                  ) : null}
                  {canSubmitLibraryForms ? (
                    <button
                      type='button'
                      className='btn btn_primary'
                      onClick={() => setShowLibraryFormModal(true)}
                    >
                      Library Form Fillup
                    </button>
                  ) : null}
                </div>
              </div>

              <div className='card-body py-4 bg-white border-top'>
                <div className='row gy-4'>
                  <div className='col-12'>
                    <div className='card shadow-none border pt-4'>
                      <div className='card-header d-flex justify-content-between align-items-center bg-white'>
                        <div>
                          <h5 className='mb-0' style={{ color: '#181C32' }}>
                            Submissions
                          </h5>
                          <div className='text-muted small mb-2'>
                            All submissions loaded by default. Use filters to refine.
                          </div>
                        </div>
                      </div>

                      <div className='px-6 pt-4 pb-2 bg-white border-bottom'>
                        <div className='row gx-3 gy-3 align-items-end'>
                          <div className='col-md-4'>
                            <label
                              className='form-label fw-semibold fs-8'
                              style={{ color: '#A1A5B7' }}
                            >
                              Template
                            </label>
                            <SearchableSelect
                              className='form-select form-select-sm'
                              value={submissionTemplateId ?? 'all'}
                              onChange={(e) => {
                                const raw = e.target.value
                                if (raw === 'all' || raw === '') {
                                  setSubmissionTemplateId(null)
                                  return
                                }
                                const nextId = Number(raw)
                                if (Number.isNaN(nextId)) {
                                  setSubmissionTemplateId(null)
                                  return
                                }
                                setSubmissionTemplateId(nextId)
                              }}
                            >
                              <option value='all'>All templates</option>
                              {templates.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {`${(t.code || '').toUpperCase()} - ${t.title}`}
                                </option>
                              ))}
                            </SearchableSelect>
                          </div>
                          <div className='col-md-4'>
                            <label
                              className='form-label fw-semibold fs-8'
                              style={{ color: '#A1A5B7' }}
                            >
                              Submitter
                            </label>
                            <SearchableSelect
                              className='form-select form-select-sm'
                              value={submittedByFilter}
                              onChange={(e) => setSubmittedByFilter(e.target.value)}
                            >
                              <option value='all'>All submitters</option>
                              {submitterOptions.map((submitter) => (
                                <option key={submitter.value} value={submitter.value}>
                                  {submitter.label}
                                </option>
                              ))}
                            </SearchableSelect>
                          </div>
                          <div className='col-md-auto d-flex align-items-end'>
                            <button
                              type='button'
                              className='btn btn-light btn-sm text-nowrap'
                              onClick={() => {
                                setSubmissionTemplateId(null)
                                setSubmittedByFilter('all')
                              }}
                              disabled={submissionTemplateId === null && submittedByFilter === 'all'}
                            >
                              <i className='fas fa-times me-1' />
                              Clear Filters
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className='card-body p-0'>
                        {loadingSubmissions || loadingTemplates ? (
                          <div className='table-responsive'>
                            <table
                              className='table table-bordered align-middle mb-0'
                              style={{ tableLayout: 'fixed', width: '100%' }}
                            >
                              <thead className='bg-light'>
                                <tr className='text-muted fw-semibold'>
                                  <th style={{ width: 240 }}>Form Number / Name</th>
                                  <th style={{ width: 180 }}>Vessel</th>
                                  <th style={{ width: 200 }}>Submitted By</th>
                                  <th style={{ width: 150 }}>Status</th>
                                  <th style={{ width: 180 }}>Submitted</th>
                                  <th className='text-center' style={{ width: 260 }}>
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                <TableSkeleton rows={8} cols={6} />
                              </tbody>
                            </table>
                          </div>
                        ) : combinedSubmissions.length === 0 ? (
                          <div className='p-4'>
                            <div className='alert alert-light mb-0'>
                              <i className='fas fa-info-circle me-2' />
                              No submissions available.
                            </div>
                          </div>
                        ) : filteredSubmissions.length === 0 ? (
                          <div className='p-4'>
                            <div className='alert alert-light mb-0'>
                              <i className='fas fa-info-circle me-2' />
                              No submissions match these filters.
                            </div>
                          </div>
                        ) : (
                          <div className='table-responsive'>
                            <table
                              className='table table-bordered align-middle mb-0'
                              style={{ tableLayout: 'fixed', width: '100%' }}
                            >
                              <thead className='bg-light'>
                                <tr className='text-muted fw-semibold'>
                                  <th style={{ width: 240 }}>Form Number / Name</th>
                                  <th style={{ width: 180 }}>Vessel</th>
                                  <th style={{ width: 200 }}>Submitted By</th>
                                  <th style={{ width: 150 }}>Status</th>
                                  <th style={{ width: 180 }}>Submitted</th>
                                  <th className='text-center' style={{ width: 260 }}>
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredSubmissions.map((row) => {
                                  const submission = row.submission
                                  const templateId = submission.templateId ?? 0
                                  const technicalTemplate = templateById.get(templateId)
                                  const libraryTemplate = libraryTemplateById.get(templateId)
                                  const identity = row.source === 'technical'
                                    ? technicalSubmissionIdentity(submission as TechnicalFormSubmission, technicalTemplate)
                                    : librarySubmissionIdentity(libraryTemplate)
                                  const primaryLabel = identity.code
                                  const secondaryLabel = identity.title
                                  const rowKey = `${row.source}-${submission.id}`

                                  return (
                                    <tr key={`${row.source}-${submission.id}`}>
                                      <td>
                                        <div className='fw-semibold' style={{ color: '#181C32' }}>
                                          {primaryLabel}
                                          {row.source === 'library' ? (
                                            <span className='badge bg-light text-dark border ms-2'>
                                              Library
                                            </span>
                                          ) : null}
                                        </div>
                                        <div className='text-muted small'>{secondaryLabel}</div>
                                      </td>
                                      <td className='text-muted'>
                                        {resolveVesselLabel(submission)}
                                      </td>
                                      <td className='text-muted'>
                                        {resolveSubmitterLabel(submission)}
                                      </td>
                                      <td>{badgeForStatus(submission.status)}</td>
                                      <td className='text-muted'>
                                        {submission.createdAt
                                          ? new Date(submission.createdAt).toLocaleString()
                                          : '-'}
                                      </td>
                                      <td className='text-center'>
                                        {row.source === 'technical' ? (
                                          <div className='d-flex justify-content-center gap-2'>
                                            <button
                                              className='btn btn-light btn-sm'
                                              type='button'
                                              onClick={() =>
                                                openView(submission as TechnicalFormSubmission)
                                              }
                                            >
                                              View
                                            </button>
                                            {canDownloadFormPdf ? (
                                              <button
                                                className='btn btn-light btn-sm'
                                                type='button'
                                                onClick={() =>
                                                  downloadSubmissionPdf(
                                                    submission as TechnicalFormSubmission
                                                  )
                                                }
                                                disabled={pdfBusy}
                                              >
                                                {isPreparingPdf('technical', submission.id) ? 'Preparing...' : 'Download PDF'}
                                              </button>
                                            ) : null}
                                            {canDeleteSubmission ? (
                                              <button
                                                className='btn btn-danger btn-sm'
                                                type='button'
                                                onClick={() => handleDelete(submission.id)}
                                                disabled={deletingKey === rowKey}
                                              >
                                                {deletingKey === rowKey
                                                  ? 'Deleting...'
                                                  : 'Delete'}
                                              </button>
                                            ) : null}
                                          </div>
                                        ) : (
                                          <div className='d-flex justify-content-center gap-2'>
                                            <button
                                              className='btn btn-light btn-sm'
                                              type='button'
                                              onClick={() =>
                                                openLibraryView(submission as QHSEFormSubmission)
                                              }
                                            >
                                              View
                                            </button>
                                            {canDownloadFormPdf ? (
                                              <button
                                                className='btn btn-light btn-sm'
                                                type='button'
                                                onClick={() =>
                                                  downloadLibrarySubmissionPdf(
                                                    submission as QHSEFormSubmission
                                                  )
                                                }
                                                disabled={pdfBusy}
                                              >
                                                {isPreparingPdf('library', submission.id) ? 'Preparing...' : 'Download PDF'}
                                              </button>
                                            ) : null}
                                            {canDeleteSubmission ? (
                                              <button
                                                className='btn btn-danger btn-sm'
                                                type='button'
                                                onClick={() => handleDeleteLibrary(submission.id)}
                                                disabled={deletingKey === rowKey}
                                              >
                                                {deletingKey === rowKey
                                                  ? 'Deleting...'
                                                  : 'Delete'}
                                              </button>
                                            ) : null}
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {pdfSubmission && pdfPayload && (
        <div
          style={{
            position: 'fixed',
            left: '-10000px',
            top: 0,
            width: '190mm',
            background: '#fff',
            visibility: 'hidden',
            zIndex: -1,
          }}
        >
          <div
            ref={pdfRef}
            className='pdf-content'
            style={{
              width: '190mm',
              padding: '8mm',
              backgroundColor: '#ffffff',
              color: '#181C32',
              fontFamily: 'Arial, sans-serif',
              boxSizing: 'border-box',
            }}
          >
            <FormCompanyLogoDisplay company={pdfCompanyLogo} showPlaceholder={false}>
              <style>
                {`
                .pdf-content, .pdf-content * {
                  box-sizing: border-box;
                }
                .pdf-content {
                  font-size: 12px;
                }
                .pdf-card {
                  break-inside: avoid;
                  page-break-inside: avoid;
                }
                table {
                  width: 100%;
                  border-collapse: collapse;
                }
                td, th {
                  vertical-align: top;
                  word-break: break-word;
                  overflow-wrap: anywhere;
                }
              `}
              </style>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 700 }}>
                    Submission #{pdfSubmission.id}
                  </div>
                  <div style={{ color: '#7e8299', fontSize: '12px' }}>
                    {pdfPayload?.formTitle || pdfTemplate?.title || 'Form Submission'}
                  </div>
                </div>
                <div style={{ textAlign: 'right', fontSize: '12px', color: '#7e8299' }}>
                  <div>{pdfPayload?.formCode || pdfTemplate?.code || '-'}</div>
                  <div>
                    {pdfSubmission.createdAt
                      ? new Date(pdfSubmission.createdAt).toLocaleString()
                      : '-'}
                  </div>
                </div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '6px', width: '50%' }}>
                      <div
                        className='pdf-card pdf-avoid'
                        style={{ border: '1px solid #e4e6ef', borderRadius: '8px', padding: '12px' }}
                      >
                        <div style={{ fontWeight: 600, marginBottom: '8px' }}>Submission Details</div>
                        <div style={{ fontSize: '12px', color: '#7e8299' }}>
                          <div>
                            Template Code:{' '}
                            <strong>{pdfPayload?.formCode || pdfTemplate?.code || '-'}</strong>
                          </div>
                          <div>
                            Version: <strong>{pdfSubmission.templateVersion || '-'}</strong>
                          </div>
                          <div>
                            Format:{' '}
                            <strong>{resolveVisibleFormat(pdfSubmission.uploadedFormat)}</strong>
                          </div>
                          <div>
                            Status: <strong>{pdfSubmission.status || '-'}</strong>
                          </div>
                        </div>
                      </div>
                    </td>

                    <td style={{ padding: '6px', width: '50%' }}>
                      <div
                        className='pdf-card pdf-avoid'
                        style={{ border: '1px solid #e4e6ef', borderRadius: '8px', padding: '12px' }}
                      >
                        <div style={{ fontWeight: 600, marginBottom: '8px' }}>Form Summary</div>
                        <div style={{ fontSize: '12px', color: '#7e8299' }}>
                          <div>
                            Form Title:{' '}
                            <strong>{pdfPayload?.formTitle || pdfTemplate?.title || '-'}</strong>
                          </div>
                          <div>
                            Submitted By: <strong>{resolveSubmitterLabel(pdfSubmission)}</strong>
                          </div>
                          <div>
                            Vessel: <strong>{resolveVesselLabel(pdfSubmission)}</strong>
                          </div>
                          <div>
                            Remarks:{' '}
                            <strong>{pdfPayload?.remarks || pdfSubmission.remarks || '-'}</strong>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div
                className='pdf-card'
                style={{ border: '1px solid #e4e6ef', borderRadius: '8px', padding: '12px' }}
              >
                <div style={{ fontWeight: 600, marginBottom: '12px' }}>Form Responses</div>

                {pdfError ? (
                  <div style={{ color: '#f1416c', fontSize: '12px' }}>{pdfError}</div>
                ) : pdfStaticForm ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>{buildPdfResponseRows(pdfStaticForm.fields, pdfPayload)}</tbody>
                  </table>
                ) : (
                  <pre
                    style={{
                      fontSize: '11px',
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {pdfSubmission.submissionData}
                  </pre>
                )}
              </div>
            </FormCompanyLogoDisplay>
          </div>
        </div>
      )}

      {showFormModal && (
        <div
          className='ff-overlay'
          onMouseDown={(e) => {
            // close only when clicking outside the panel
            if (e.target === e.currentTarget) setShowFormModal(false)
          }}
        >
          <div
            className='ff-panel'
            role='dialog'
            aria-modal='true'
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className='ff-header'>
              <div>
                <div className='ff-title'>Form Fillup</div>
                <div className='ff-subtitle'>Fill and submit technical forms.</div>
              </div>

              <button
                className='ff-close'
                type='button'
                onClick={() => setShowFormModal(false)}
                aria-label='Close'
              >
                ✕
              </button>
            </div>

            <div className='ff-body'>
              <StaticFormRenderer
                templates={templates}
                refreshTemplates={loadTemplates as any}
                onTemplateCreated={handleTemplateCreated as any}
                onSubmissionUploaded={refreshSubmissions}
                submissionContext={submissionContext as any}
                canSubmit={canFillStaticForm as any}
              />
            </div>
          </div>

          {/* ✅ Modal CSS */}
          <style>{`
      .ff-overlay {
        position: fixed;
        inset: 0;
        z-index: 1050;
        background: rgba(0,0,0,0.55);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 18px;
      }

      .ff-panel {
        width: min(1400px, calc(100vw - 36px));   /* ✅ actually wide */
        height: min(88vh, 900px);                /* ✅ good height */
        background: #fff;
        border-radius: 14px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.25);
        display: flex;
        flex-direction: column;
        overflow: hidden;                         /* ✅ prevents weird scroll edges */
      }

      .ff-header {
        padding: 18px 20px 10px 20px;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        border-bottom: 1px solid #eef0f5;
      }

      .ff-title {
        font-size: 18px;
        font-weight: 700;
        color: #181C32;
        line-height: 1.2;
      }

      .ff-subtitle {
        font-size: 12px;
        color: #7e8299;
        margin-top: 4px;
      }

      .ff-close {
        border: none;
        background: #f5f8fa;
        color: #181C32;
        width: 36px;
        height: 36px;
        border-radius: 10px;
        cursor: pointer;
        font-size: 18px;
        line-height: 1;
        display: grid;
        place-items: center;
      }

      .ff-close:hover {
        background: #eef2f7;
      }

      .ff-body {
        padding: 14px 20px 20px 20px;
        overflow: auto;                          /* ✅ scroll inside modal */
      }

      /* Mobile tuning */
      @media (max-width: 768px) {
        .ff-overlay { padding: 10px; }
        .ff-panel {
          width: calc(100vw - 20px);
          height: calc(100vh - 20px);
          border-radius: 12px;
        }
        .ff-body { padding: 12px; }
      }
    `}</style>
        </div>
      )}

      {showLibraryFormModal && (
        <LibraryFormSubmissionModal
          isOpen={showLibraryFormModal}
          onClose={() => setShowLibraryFormModal(false)}
          companyGroupAdminId={companyGroupAdminId}
          companyAdminId={companyAdminId}
          vesselId={submissionContext.vesselId}
          crewId={submissionContext.crewId}
          createdByUserId={currentUser?.id}
          onSubmitted={refreshSubmissions}
        />
      )}

      {/* View modal (CategoryModal style) */}
      {viewSubmission && (
        <div
          className='modal fade show d-flex align-items-center justify-content-center'
          tabIndex={-1}
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1050,
          }}
        >
          <div
            className='modal-dialog modal-xxl modal-dialog-centered'
            role='document'
            style={{
              maxWidth: '1200px',
              width: '90%',
              margin: '0 auto',
            }}
          >
            <div className='modal-content bg-white w-100' style={{ color: '#181C32' }}>
              <div className='modal-header border-0 pb-0'>
                <div>
                  <h3 className='modal-title fw-bold text-dark mb-1'>
                    Submission #{viewSubmission.id}
                  </h3>
                  <p className='text-muted mb-0 fs-7'>
                    Review the submitted form details and responses.
                  </p>
                </div>
                <button type='button' className='btn-close' onClick={closeView}></button>
              </div>

              <div className='modal-body pt-3' style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <FormCompanyLogo
                  vesselId={getVesselId(viewSubmission)}
                  emptyMessage='No vessel linked to this submission.'
                >
                  <div
                    className='mb-4'
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                      gap: '16px',
                    }}
                  >
                    <div>
                      <div
                        className='h-100 rounded-3 p-4 shadow-sm border'
                        style={{ background: 'linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 80%)' }}
                      >
                        <div className='d-flex align-items-center justify-content-between mb-3'>
                          <h6 className='fw-bold text-primary mb-0'>Submission Details</h6>
                          <span className='badge bg-light-primary text-primary'>Record</span>
                        </div>
                        <div className='mb-3'>
                          <label className='form-label fw-semibold text-muted fs-8 mb-1'>
                            Template Code
                          </label>
                          <div className='fw-bold text-dark'>
                            {technicalSubmissionIdentity(viewSubmission, viewTemplate).code}
                          </div>
                        </div>
                        <div className='row g-3 mb-3'>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>
                              Version
                            </label>
                            <div className='fw-bold text-dark'>
                              {viewSubmission.templateVersion || '-'}
                            </div>
                          </div>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>
                              Format
                            </label>
                            <div className='fw-bold text-dark'>
                              {resolveVisibleFormat(viewSubmission.uploadedFormat)}
                            </div>
                          </div>
                        </div>
                        <div className='row g-3'>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>
                              Status
                            </label>
                            <div>{badgeForStatus(viewSubmission.status)}</div>
                          </div>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>
                              Submitted
                            </label>
                            <div className='fw-bold text-dark'>
                              {viewSubmission.createdAt
                                ? new Date(viewSubmission.createdAt).toLocaleString()
                                : '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div
                        className='h-100 rounded-3 p-4 shadow-sm border'
                        style={{ background: 'linear-gradient(180deg, #FDF9FF 0%, #FFFFFF 80%)' }}
                      >
                        <div className='d-flex align-items-center justify-content-between mb-3'>
                          <h6 className='fw-bold text-primary mb-0'>Form Summary</h6>
                          <span className='badge bg-light text-primary border'>Overview</span>
                        </div>
                        <div className='mb-3'>
                          <label className='form-label fw-semibold text-muted fs-8 mb-1'>
                            Form Title
                          </label>
                          <div className='fw-bold text-dark'>
                            {technicalSubmissionIdentity(viewSubmission, viewTemplate).title}
                          </div>
                        </div>
                        <div className='row g-3 mb-3'>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>
                              Submitted By
                            </label>
                            <div className='fw-bold text-dark'>{resolveSubmitterLabel(viewSubmission)}</div>
                          </div>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>
                              Vessel
                            </label>
                            <div className='fw-bold text-dark'>{resolveVesselLabel(viewSubmission)}</div>
                          </div>
                        </div>
                        <div className='mb-0'>
                          <label className='form-label fw-semibold text-muted fs-8 mb-1'>
                            Remarks
                          </label>
                          <StructuredRemarksView text={viewPayload?.remarks || viewSubmission.remarks} emptyMessage='-' />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='card border shadow-sm'>
                    <div className='card-header border-0 pb-0'>
                      <div>
                        <h6 className='fw-bold text-primary mb-1'>Form Responses</h6>
                        <p className='text-muted mb-0 fs-7'>Captured answers from the submission.</p>
                      </div>
                    </div>
                    <div className='card-body pt-3'>
                      {viewError ? (
                        <div className='alert alert-danger mb-0'>
                          <i className='fas fa-exclamation-triangle me-2' />
                          {viewError}
                        </div>
                      ) : !viewPayload ? (
                        <div className='alert alert-light mb-0'>
                          <i className='fas fa-info-circle me-2' />
                          No inline data. Download the file to view this submission.
                        </div>
                      ) : viewStaticForm ? (
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                            gap: '12px',
                            width: '100%',
                          }}
                        >
                          {viewStaticForm.fields.map((field) => (
                            <div
                              key={field.name}
                              style={{
                                gridColumn: field.type === 'checklist' ? '1 / -1' : 'auto',
                                minWidth: 0,
                              }}
                            >
                              <div className='border rounded-3 p-3 h-100'>
                                <div className='fw-semibold text-dark mb-1'>{field.label}</div>
                                <div className='text-muted small'>{renderFieldValue(field)}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <pre
                          className='bg-light p-3 rounded small mb-0'
                          style={{ whiteSpace: 'pre-wrap' }}
                        >
                          {viewSubmission.submissionData}
                        </pre>
                      )}
                    </div>
                  </div>
                </FormCompanyLogo>
              </div>

              <div className='modal-footer border-0'>
                {canDownloadFormPdf ? (
                  <button
                    className='btn btn-light btn-sm'
                    type='button'
                    onClick={() => downloadSubmissionPdf(viewSubmission)}
                    disabled={pdfBusy}
                  >
                    {isPreparingPdf('technical', viewSubmission.id) ? 'Preparing...' : 'Download PDF'}
                  </button>
                ) : null}
                <button className='btn btn_primary btn-sm' type='button' onClick={closeView}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {pdfLibrarySubmission && (
        <div
          style={{
            position: 'fixed',
            left: '-10000px',
            top: 0,
            width: '190mm',
            background: '#fff',
            visibility: 'hidden',
            zIndex: -1,
          }}
        >
          <div
            ref={pdfLibraryRef}
            className='pdf-content'
            style={{
              width: '190mm',
              padding: '8mm',
              backgroundColor: '#ffffff',
              color: '#181C32',
              fontFamily: 'Arial, sans-serif',
              boxSizing: 'border-box',
            }}
          >
            <FormCompanyLogoDisplay company={pdfCompanyLogo} showPlaceholder={false}>
              <style>
                {`
                .pdf-content, .pdf-content * {
                  box-sizing: border-box;
                }
                .pdf-content {
                  font-size: 12px;
                }
                .pdf-card {
                  break-inside: avoid;
                  page-break-inside: avoid;
                }
                table {
                  width: 100%;
                  border-collapse: collapse;
                }
                td, th {
                  vertical-align: top;
                  word-break: break-word;
                  overflow-wrap: anywhere;
                }
              `}
              </style>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 700 }}>
                    Submission #{pdfLibrarySubmission.id}
                  </div>
                  <div style={{ color: '#7e8299', fontSize: '12px' }}>
                    {libraryTemplateById.get(pdfLibrarySubmission.templateId ?? 0)?.name ||
                      'Library Submission'}
                  </div>
                </div>
                <div style={{ textAlign: 'right', fontSize: '12px', color: '#7e8299' }}>
                  <div>
                    {libraryTemplateById.get(pdfLibrarySubmission.templateId ?? 0)?.formType || '-'}
                  </div>
                  <div>
                    {pdfLibrarySubmission.createdAt
                      ? new Date(pdfLibrarySubmission.createdAt).toLocaleString()
                      : '-'}
                  </div>
                </div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '6px', width: '50%' }}>
                      <div
                        className='pdf-card pdf-avoid'
                        style={{ border: '1px solid #e4e6ef', borderRadius: '8px', padding: '12px' }}
                      >
                        <div style={{ fontWeight: 600, marginBottom: '8px' }}>Submission Details</div>
                        <div style={{ fontSize: '12px', color: '#7e8299' }}>
                          <div>
                            Template:{' '}
                            <strong>
                              {libraryTemplateById.get(pdfLibrarySubmission.templateId ?? 0)?.name ||
                                `Template #${pdfLibrarySubmission.templateId ?? '-'}`}
                            </strong>
                          </div>
                          <div>
                            Status: <strong>{pdfLibrarySubmission.status || '-'}</strong>
                          </div>
                          <div>
                            Submitted By: <strong>{resolveSubmitterLabel(pdfLibrarySubmission)}</strong>
                          </div>
                          <div>
                            Vessel: <strong>{resolveVesselLabel(pdfLibrarySubmission)}</strong>
                          </div>
                        </div>
                      </div>
                    </td>

                    <td style={{ padding: '6px', width: '50%' }}>
                      <div
                        className='pdf-card pdf-avoid'
                        style={{ border: '1px solid #e4e6ef', borderRadius: '8px', padding: '12px' }}
                      >
                        <div style={{ fontWeight: 600, marginBottom: '8px' }}>Form Summary</div>
                        <div style={{ fontSize: '12px', color: '#7e8299' }}>
                          <div>
                            Form Type:{' '}
                            <strong>
                              {libraryTemplateById.get(pdfLibrarySubmission.templateId ?? 0)
                                ?.formType || '-'}
                            </strong>
                          </div>
                          <div>
                            Remarks: <strong>{pdfLibrarySubmission.remarks || '-'}</strong>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div
                className='pdf-card'
                style={{ border: '1px solid #e4e6ef', borderRadius: '8px', padding: '12px' }}
              >
                <div style={{ fontWeight: 600, marginBottom: '12px' }}>Form Responses</div>
                <table>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', paddingBottom: '6px' }}>Field</th>
                      <th style={{ textAlign: 'left', paddingBottom: '6px' }}>Value</th>
                    </tr>
                  </thead>
                  <tbody>{renderLibraryValueRows(pdfLibraryFields, pdfLibraryValues)}</tbody>
                </table>
              </div>
            </FormCompanyLogoDisplay>
          </div>
        </div>
      )}

      {viewLibrarySubmission && (
        <div
          className='modal fade show d-flex align-items-center justify-content-center'
          tabIndex={-1}
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1050,
          }}
        >
          <div
            className='modal-dialog modal-xxl modal-dialog-centered'
            role='document'
            style={{
              maxWidth: '1200px',
              width: '90%',
              margin: '0 auto',
            }}
          >
            <div className='modal-content bg-white w-100' style={{ color: '#181C32' }}>
              <div className='modal-header border-0 pb-0'>
                <div>
                  <h3 className='modal-title fw-bold text-dark mb-1'>
                    Library Submission #{viewLibrarySubmission.id}
                  </h3>
                  <p className='text-muted mb-0 fs-7'>
                    Review the submitted library form details and responses.
                  </p>
                </div>
                <button type='button' className='btn-close' onClick={closeLibraryView}></button>
              </div>

              <div className='modal-body pt-3' style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <FormCompanyLogo
                  vesselId={getVesselId(viewLibrarySubmission)}
                  emptyMessage='No vessel linked to this submission.'
                >
                  <div
                    className='mb-4'
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                      gap: '16px',
                    }}
                  >
                    <div>
                      <div
                        className='h-100 rounded-3 p-4 shadow-sm border'
                        style={{ background: 'linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 80%)' }}
                      >
                        <div className='d-flex align-items-center justify-content-between mb-3'>
                          <h6 className='fw-bold text-primary mb-0'>Submission Details</h6>
                          <span className='badge bg-light-primary text-primary'>Library</span>
                        </div>
                        <div className='mb-3'>
                          <label className='form-label fw-semibold text-muted fs-8 mb-1'>
                            Template
                          </label>
                          <div className='fw-bold text-dark'>
                            {libraryTemplateById.get(viewLibrarySubmission.templateId ?? 0)?.name ||
                              `Template #${viewLibrarySubmission.templateId ?? '-'}`}
                          </div>
                        </div>
                        <div className='row g-3'>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>
                              Status
                            </label>
                            <div>{badgeForStatus(viewLibrarySubmission.status)}</div>
                          </div>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>
                              Submitted
                            </label>
                            <div className='fw-bold text-dark'>
                              {viewLibrarySubmission.createdAt
                                ? new Date(viewLibrarySubmission.createdAt).toLocaleString()
                                : '-'}
                            </div>
                          </div>
                        </div>
                        <div className='row g-3 mt-1'>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>
                              Submitted By
                            </label>
                            <div className='fw-bold text-dark'>
                              {resolveSubmitterLabel(viewLibrarySubmission)}
                            </div>
                          </div>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>
                              Vessel
                            </label>
                            <div className='fw-bold text-dark'>
                              {resolveVesselLabel(viewLibrarySubmission)}
                            </div>
                          </div>
                        </div>
                        <div className='mt-3'>
                          <label className='form-label fw-semibold text-muted fs-8 mb-1'>
                            Remarks
                          </label>
                          <StructuredRemarksView text={viewLibrarySubmission.remarks} emptyMessage='-' />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='card border shadow-sm'>
                    <div className='card-header border-0 pb-0'>
                      <div>
                        <h6 className='fw-bold text-primary mb-1'>Form Responses</h6>
                        <p className='text-muted mb-0 fs-7'>
                          Captured answers from the library submission.
                        </p>
                      </div>
                    </div>
                    <div className='card-body pt-3'>
                      {viewLibraryLoading ? (
                        <div className='d-flex align-items-center gap-2 text-muted'>
                          <span className='spinner-border spinner-border-sm' />
                          Loading responses...
                        </div>
                      ) : viewLibraryError ? (
                        <div className='alert alert-danger mb-0'>
                          <i className='fas fa-exclamation-triangle me-2' />
                          {viewLibraryError}
                        </div>
                      ) : viewLibraryFields.length === 0 ? (
                        <div className='alert alert-light mb-0'>
                          <i className='fas fa-info-circle me-2' />
                          No responses available for this submission.
                        </div>
                      ) : (
                        <div className='table-responsive'>
                          <table className='table table-bordered align-middle mb-0'>
                            <thead className='table-light'>
                              <tr>
                                <th style={{ width: '45%' }}>Field</th>
                                <th style={{ width: '55%' }}>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {renderLibraryValueRows(viewLibraryFields, viewLibraryValues)}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </FormCompanyLogo>
              </div>

              <div className='modal-footer border-0'>
                {canDownloadFormPdf ? (
                  <button
                    className='btn btn-light btn-sm'
                    type='button'
                    onClick={() => downloadLibrarySubmissionPdf(viewLibrarySubmission)}
                    disabled={pdfBusy}
                  >
                    {isPreparingPdf('library', viewLibrarySubmission.id) ? 'Preparing...' : 'Download PDF'}
                  </button>
                ) : null}
                <button className='btn btn_primary btn-sm' type='button' onClick={closeLibraryView}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        tone={confirmState.tone}
        confirmLabel='Delete'
        busy={confirmBusy}
        onCancel={() => setConfirmState((prev) => ({ ...prev, open: false }))}
        onConfirm={async () => {
          try {
            setConfirmBusy(true)
            await confirmState.onConfirm()
          } catch (err: any) {
            showErrorToast(err, 'Unable to delete submission.', 'delete')
          } finally {
            setConfirmBusy(false)
            setDeletingKey(null)
            setConfirmState((prev) => ({ ...prev, open: false }))
          }
        }}
      />
      <ToastContainer
        position='top-center'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ top: '5rem' }}
      />
    </>
  )
}

export default TechnicalFormsPage
